import Rete from '@obvious.tech/rete'

import { sockets, definitions } from '../..'
import NodeTypizer from '../../Components/NodeTypizer'
import DefaultNode from '../../Components/CustomNode'
import OutputWithDecompose from '../../Outputs/OutputWithDecompose'
import createEventName from '../../../../helpers/prepareEventName'
import { getSocketFromType } from '../../Sockets/utils'
import CheckboxesModalControl from '../../Controls/Modal/CheckboxesModal'
import SelectControl from '../../Controls/SelectControl'
import {
  getPossibleComparisons,
  comparisonFunctions,
} from '../../../../helpers/ComparisonHelper'
import { getEventOutputs } from './eventOutputs'
import { hasDeprecatedCreatorIdOutput } from '../../../../helpers/legacyTriggerOutputsHelper'

const addOutputToNode = (node, eventOutputs, editor) => {
  eventOutputs.forEach(({ key, label, socket, decompose }) => {
    const name = label ?? key

    node.addOutput(
      decompose === true
        ? new OutputWithDecompose(key, name, socket, editor)
        : new Rete.Output(key, name, socket),
    )
  })
}

export default class ConstellationEntityEventComponent extends Rete.Component {
  constructor(options) {
    const { microservice, entity, event, type } = options
    const name = createEventName(microservice, entity, event, type)
    super(name)
    this.data.component = NodeTypizer(DefaultNode, {
      type: 'trigger',
    })
    this.currentTask = null
    this.entity = entity
    this.event = event
    // The task map is per component, so it declares the deprecated output too:
    // only the nodes that already wire it actually get the socket.
    const outputs = getEventOutputs(event, entity, sockets, {
      deprecatedCreatorId: true,
    }).reduce(
      (acc, { key }) => ({
        ...acc,
        [key]: 'output',
      }),
      {
        act: 'option',
      },
    )
    this.task = {
      outputs,
      init: task => {
        this.currentTask = task
      },
    }
    this.contextMenuPath = ['Triggers', 'Constellation', microservice, entity]
  }

  getPropertiesforEntity = (node, entity = null) => {
    const { properties = [] } = definitions.entities[entity || node.data.entity]
    return properties.map(p => {
      return {
        key: p.name,
        name: p.name,
      }
    })
  }

  createAndAddInput = (node, entity, name) => {
    const { properties = [] } = definitions.entities[entity || node.data.entity]
    const property = properties.find(p => p.name === name)

    const filters = getPossibleComparisons(property.type, property.entity).map(
      comparison => ({
        value: comparison,
        label: comparison,
      }),
    )

    const { args: comparisonFunctionArguments } = comparisonFunctions.equals(
      property.type,
      property.entity,
    )
    const inputSocket = comparisonFunctionArguments.length
      ? comparisonFunctionArguments.length > 1
        ? sockets[`${property.entity}CallbackSocket`]
        : getSocketFromType(property.type, property.entity, property.format)
      : sockets.anyTypeSocket
    const inp = new Rete.Input(property.name, property.name, inputSocket)
    inp.addControl(
      new SelectControl(
        this.editor,
        node,
        property.name,
        property.name,
        newComparisonKey => {
          // node.data[property.name] = newComparisonKey;
          const { args } = comparisonFunctions[newComparisonKey](
            property.type,
            property.entity,
          )
          inp.socket =
            sockets[
              args.length > 1
                ? `${property.entity || property.type}CallbackSocket`
                : args.length
                  ? `${args[0]}Socket`
                  : 'noTypeSocket'
            ]
          node.update()

          setTimeout(() => {
            this.editor.view.updateConnections({
              node,
            })
          }, 1)
        },
        filters,
        filters[0].value,
      ),
    )
    node.addInput(inp)
  }

  handleInput =
    (editor, node, entity) =>
    ({ name, input }, checked) => {
      if (checked && !node.inputs.get(name)) {
        this.createAndAddInput(node, entity, name)
      } else if (!checked && node.inputs.get(name)) {
        node.inputs
          .get(name)
          .connections.map(connection => editor.removeConnection(connection))
        node.removeInput(node.inputs.get(name))
        if (node.data[name]) {
          delete node.data[name]
        }
      }
      node.update()
      setTimeout(() => {
        editor.view.updateConnections({
          node,
        })
      }, 1)
    }

  building(node, entity) {
    node.controls.forEach(c => {
      node.removeControl(c)
    })
    this.checkboxes = this.getPropertiesforEntity(node, entity)
    node.addControl(
      new CheckboxesModalControl(
        this.editor,
        node,
        'inputs',
        this.handleInput(this.editor, node, entity),
        this.checkboxes,
        'Conditions',
      ),
    )
    setTimeout(() => {
      node && node.update()
      this.editor.view.updateConnections({
        node,
      })
    }, 1)
  }

  builder(node) {
    node.data = {
      inputs: {},
      ...node.data,
    }
    node.addOutput(new Rete.Output('act', 'Then', sockets.actionSocket))
    // `editor.silent` is only true while a diagram is being imported, so a node
    // the user drops on the canvas never gets the deprecated output (#6070).
    const deprecatedCreatorId =
      this.editor?.silent === true && hasDeprecatedCreatorIdOutput(node.id)

    addOutputToNode(
      node,
      getEventOutputs(this.event, this.entity, sockets, {
        deprecatedCreatorId,
      }),
      this.editor,
    )
    this.building(node, this.entity)
    if (node.data.inputs) {
      Object.keys(node.data.inputs)
        .filter(key => node.data.inputs[key])
        .forEach(key => {
          this.createAndAddInput(node, this.entity, key)
        })
    }
  }

  worker(node, inputs, outputs) {}
}
